import { useState, useEffect } from "react";
import { Edit, Plus, Upload, Camera, X } from "lucide-react";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import UsersTable from "../Components/UserTable";
import type { UserAccount, UserForm, Role } from "../types/tenant";
import apiClient from "../services/apiClient";
import { API_ENDPOINTS } from "../config/api";
import { useTranslation } from "react-i18next";


// -----------------------------
// Profile Page Component
// -----------------------------
const ProfilePage = () => {
    const { t } = useTranslation();
    const [currentUser, setCurrentUser] = useState<UserAccount | null>(null);
    const [users, setUsers] = useState<UserAccount[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<UserAccount | null>(null);
    const [isSavingUser, setIsSavingUser] = useState(false);
    const [form, setForm] = useState<UserForm>({
        name: "",
        email: "",
        role_id: "",
        password: "",
        must_change_password: false,
    });

    const isAdmin = currentUser?.role?.role_id === 1 || currentUser?.role?.role_id === 2;

    const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const roles: Role[] = [
        { role_id: 1, name: "Super Admin", description: "System role: Super" },
        { role_id: 2, name: "Admin", description: "System role: Admin" },
        { role_id: 3, name: "User", description: "System role: User" },
    ];
    const selectableRoles = roles.filter(
        (role) => role.role_id !== 1
    );
    const getRoleName = (role: Role) => {
        const roleKeys: Record<number, string> = {
            1: "superAdmin",
            2: "admin",
            3: "user",
        };

        const roleKey = roleKeys[role.role_id];
        return roleKey ? t(`profilePage.roles.${roleKey}`) : role.name;
    };

    if (!import.meta.env.VITE_BACKEND_URL) {
        throw new Error("VITE_BACKEND_URL is not defined");
    }
    const BASE_URL = import.meta.env.VITE_BACKEND_URL;

    const canEditUser = (targetUser: UserAccount): boolean => {
        if (!currentUser) return false;

        // 1. No one can edit themselves (explicit rule)
        if (targetUser.user_id === currentUser.user_id) {
            return false;
        }

        // 2. Normal users cannot edit anyone
        if (currentUser.role.role_id === 3) {
            return false;
        }

        // 3. Admin cannot edit Super Admin
        if (
            currentUser.role.role_id === 2 &&
            targetUser.role.role_id === 1
        ) {
            return false;
        }

        // 4. Super Admin can edit Admin & User
        if (currentUser.role.role_id === 1) {
            return true;
        }

        // 5. Admin can edit User
        if (currentUser.role.role_id === 2 && targetUser.role.role_id === 3) {
            return true;
        }

        return false;
    };

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) return;

        const fetchData = async () => {
            try {
                // Get current logged-in user
                const meRes = await apiClient.get<UserAccount>(API_ENDPOINTS.ME, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setCurrentUser(meRes.data);
                const loggedInUser = meRes.data;
                setCurrentUser(loggedInUser);

                // Get all tenant users
                const usersRes = await apiClient.get<UserAccount[]>(API_ENDPOINTS.TENANT_USERS);
                const filteredUsers = usersRes.data.filter(
                    (user) => user.user_id !== loggedInUser.user_id
                );
                setUsers(filteredUsers);

            } catch (err) {
                console.error("Error fetching user data:", err);
            }
        };

        fetchData();
    }, []);

    // -----------------------------
    // Modal Functions
    // -----------------------------
    const openModal = (user: UserAccount | null = null) => {

        if (user && user.user_id === currentUser?.user_id) {
            alert(t("profilePage.alerts.cannotUpdateOwnProfile"));
            return;
        }

        if (user && !canEditUser(user)) {
            alert(t("profilePage.alerts.notAllowedToUpdateUser"));
            return;
        }

        setEditingUser(user);
        setForm({
            name: user?.name || "",
            email: user?.email || "",
            role_id: user?.role?.role_id || "",
            password: "",
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async () => {

        if (editingUser && editingUser.user_id === currentUser?.user_id) {
            alert(t("profilePage.alerts.selfProfileUpdateNotAllowed"));
            return;
        }

        if (editingUser && !canEditUser(editingUser)) {
            alert(t("profilePage.alerts.unauthorizedAction"));
            return;
        }

        const token = localStorage.getItem("token");
        if (!token) return;

        // Basic validation
        if (!form.name || !form.email || (!editingUser && !form.password) || !form.role_id) {
            alert(t("profilePage.alerts.requiredFields"));
            return;
        }
        setIsSavingUser(true);
        try {

            if (editingUser) {
                // Update user
                const res = await apiClient.put<UserAccount>(
                    API_ENDPOINTS.TENANT_USER_BY_ID(editingUser.user_id),
                    {
                        name: form.name,
                        email: form.email,
                        role_id: form.role_id,
                        must_change_password: form.must_change_password,
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setUsers(users.map(u => (u.user_id === res.data.user_id ? res.data : u)));
            } else {
                // Create new user
                const res = await apiClient.post<UserAccount>(
                    API_ENDPOINTS.TENANT_USERS,
                    {
                        user: {
                            name: form.name,
                            email: form.email,
                            must_change_password: form.must_change_password ?? false,
                        },
                        role_id: form.role_id,
                        password: form.password,

                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setUsers([...users, res.data]);
            }
            setIsModalOpen(false);
            setForm({ name: "", email: "", role_id: "", password: "" });
        } catch (err: any) {
            alert(err.response?.data?.detail || t("profilePage.alerts.errorSavingUser"));
        } finally {
            setIsSavingUser(false);
        }
    };

    const handleDelete = async (user_id: number) => {
        const token = localStorage.getItem("token");
        if (!token) return;

        try {
            await apiClient.delete(API_ENDPOINTS.TENANT_USER_BY_ID(user_id));
            setUsers(users.filter(u => u.user_id !== user_id));
        } catch (err: any) {
            alert(err.response?.data?.detail || t("profilePage.alerts.errorDeletingUser"));
        }
    };

    // Handle profile photo upload
    const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert(t("profilePage.alerts.selectImageFile"));
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert(t("profilePage.alerts.fileSizeLimit"));
            return;
        }

        setUploadingPhoto(true);

        try {
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePhoto(reader.result as string);
            };
            reader.readAsDataURL(file);

            setUploadingPhoto(false);
        } catch (err: any) {
            alert(err.response?.data?.detail || t("profilePage.alerts.errorUploadingPhoto"));
            setUploadingPhoto(false);
        }
    };

    const removePhoto = () => {
        setProfilePhoto(null);
        const input = document.getElementById("photo-upload") as HTMLInputElement;
        if (input) input.value = "";
    };

    const handleStatusToggle = async (user: UserAccount) => {
        const token = localStorage.getItem("token");
        if (!token) return;

        const newStatus = user.status === "active" ? "inactive" : "active";

        try {
            const res = await apiClient.patch<UserAccount>(
                API_ENDPOINTS.TENANT_USER_STATUS(user.user_id),
                { status: newStatus }
            );

            // 🔥 Instant UI update (same as jobs)
            setUsers(prev =>
                prev.map(u =>
                    u.user_id === user.user_id
                        ? { ...u, status: res.data.status }
                        : u
                )
            );
        } catch (err) {
            console.error("Failed to toggle user status", err);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />

            <div className="max-w-[83rem] mx-auto px-4 sm:px-6 lg:px-[0rem] py-8">
                {currentUser && (
                    <div className="mb-8 bg-white border border-gray-200 rounded-3xl p-8 shadow-sm text-gray-900">
                        <div className="grid grid-cols-1 xl:grid-cols-5 gap-2 xl:gap-2 items-start">
                            <div className="xl:col-span-2">
                                <h2 className="text-2xl text-primary font-bold mb-2">
                                    {t("profilePage.welcome", { name: currentUser.name })}
                                </h2>
                                <p className="text-gray-700 text-sm ">
                                    {t("profilePage.subscriptionPrefix")}{" "}
                                    <span className="font-semibold">
                                        {currentUser.tenant.subscription_plan || t("profilePage.freePlan")}
                                    </span>{" "}
                                    {t("profilePage.subscriptionSuffix")}
                                </p>
                            </div>

                            <div className="flex flex-col gap-3 xl:col-span-1 items-start xl:pl-4">
                                <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border border-gray-300 flex items-center justify-center shadow-sm">
                                    {profilePhoto ? (
                                        <img
                                            src={profilePhoto}
                                            alt={t("profilePage.profilePhotoAlt")}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <Camera className="w-10 h-10 text-gray-400" />
                                    )}
                                </div>

                                <label
                                    htmlFor="photo-upload"
                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-md hover:bg-blue-800 hover:text-secondary transition-colors cursor-pointer text-xs shadow-sm ml-2"
                                >
                                    <Upload size={14} />
                                    {t("profilePage.upload")}
                                </label>

                                <input
                                    id="photo-upload"
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePhotoUpload}
                                    className="hidden"
                                />

                                {profilePhoto && (
                                    <button
                                        onClick={removePhoto}
                                        className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                                    >
                                        <X size={12} /> {t("profilePage.remove")}
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4 border border-gray-200 rounded-xl p-4 bg-gray-50 
                                        xl:col-span-2 xl:-ml-6 relative">
                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-500">{t("profilePage.fullName")}</span>
                                    <span className="font-semibold text-gray-900 text-sm">{currentUser.name}</span>
                                </div>

                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-500">{t("profilePage.email")}</span>
                                    <span className="font-semibold text-gray-900 text-sm">{currentUser.email}</span>
                                </div>

                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-500">{t("profilePage.organization")}</span>
                                    <span className="font-semibold text-gray-900 text-sm">{currentUser.tenant.name}</span>
                                </div>

                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-500">{t("profilePage.role")}</span>
                                    <span className="font-semibold text-gray-900 text-sm">{getRoleName(currentUser.role)}</span>
                                </div>

                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-500">{t("profilePage.subscriptionStart")}</span>
                                    <span className="font-semibold text-gray-900 text-sm">
                                        {currentUser.tenant.subscription_start_date || "-"}
                                    </span>
                                </div>

                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-500">{t("profilePage.subscriptionEnd")}</span>
                                    <span className="font-semibold text-gray-900 text-sm">
                                        {currentUser.tenant.subscription_end_date || "-"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {isAdmin && (
                    <div className="mb-8 bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">{t("profilePage.usersYouCanManage")}</h3>
                            <button
                                onClick={() => openModal(null)}
                                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-800 hover:text-secondary transition-colors flex items-center space-x-2 shadow-sm"
                            >
                                <Plus className="w-4 h-4" />
                                <span>{t("profilePage.addUser")}</span>
                            </button>
                        </div>
                        <UsersTable users={users} openModal={openModal} handleDelete={handleDelete} handleStatusToggle={handleStatusToggle} />
                    </div>
                )}

                {isModalOpen && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="relative bg-white border border-gray-200 rounded-xl shadow-xl w-96 p-6">
                            <h3 className="text-lg font-semibold mb-4 text-gray-900">
                                {editingUser ? t("profilePage.editUser") : t("profilePage.addNewUser")}
                            </h3>
                            <div className="space-y-4">
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700">
                                        {t("profilePage.fullName")} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder={t("profilePage.namePlaceholder")}
                                        value={form.name}
                                        onChange={e => setForm({ ...form, name: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white text-gray-900 placeholder-gray-400"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700">
                                        {t("profilePage.email")} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        placeholder={t("profilePage.emailPlaceholder")}
                                        value={form.email}
                                        onChange={e => setForm({ ...form, email: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white text-gray-900 placeholder-gray-400"
                                    /></div>
                                {!editingUser && (
                                    <div className="space-y-1">
                                        <label className="text-sm font-medium text-gray-700">
                                            {t("profilePage.password")} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            placeholder={t("profilePage.passwordPlaceholder")}
                                            value={form.password}
                                            onChange={e => setForm({ ...form, password: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white text-gray-900 placeholder-gray-400"
                                        />
                                    </div>
                                )}
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700">
                                        {t("profilePage.role")} <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={form.role_id}
                                        onChange={(e) => setForm({ ...form, role_id: parseInt(e.target.value) })}
                                        className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white text-gray-900"
                                    >
                                        <option value="">{t("profilePage.selectRole")}</option>
                                        {selectableRoles.map((role) => (
                                            <option key={role.role_id} value={role.role_id}>
                                                {getRoleName(role)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            {!editingUser && (
                                <div className="flex items-center space-x-2 mt-4">
                                    <input
                                        type="checkbox"
                                        checked={form.must_change_password || false}
                                        onChange={(e) => setForm({ ...form, must_change_password: e.target.checked })}
                                        className="w-4 h-4 accent-primary text-primary focus:ring-primary border-gray-300 rounded"
                                    />
                                    <label className="text-sm text-gray-600">
                                        {t("profilePage.requirePasswordChange")}
                                    </label>
                                </div>
                            )}
                            <div className="flex justify-end mt-6 space-x-2">
                                <button
                                    onClick={() => !isSavingUser && setIsModalOpen(false)}
                                    disabled={isSavingUser}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {t("profilePage.cancel")}
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={isSavingUser}
                                    className={`px-4 py-2 rounded-lg transition-colors shadow-sm text-white
                                    ${isSavingUser ? "bg-gray-400 cursor-not-allowed" : "bg-primary hover:bg-blue-800"}`}
                                >
                                    {isSavingUser ? t("profilePage.saving") : t("profilePage.save")}
                                </button>
                            </div>
                        </div>
                        {isSavingUser && (
                            <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-xl z-50">
                                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
};

export default ProfilePage;
